import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ErreurApi, EVENEMENT_SESSION_EXPIREE, appelApi, urlApi } from './api';
import { parametresVueGlobale } from './useVueGlobale';
import type { Profil } from './types';

/**
 * Taux d'absentéisme (TF-461) : par apprenant, promotion, bloc et formation,
 * sur une période, et ses exports Excel et PDF pour les financeurs. Les taux,
 * les heures et le repère du seuil sont des lectures de l'API
 * (`domain/absenteisme.ts`) : l'écran affiche, il ne recalcule rien.
 */

export type AxeAbsenteisme = 'APPRENANT' | 'PROMOTION' | 'MODULE' | 'FORMATION';
export type ChoixPeriodeAbsenteisme = 'DEBUT' | 'MOIS' | 'MOIS_PRECEDENT' | 'TRIMESTRE' | 'ANNEE' | 'LIBRE';
export type TriAbsenteisme = 'NOM' | 'TAUX';

export interface MesureAbsenteisme {
  taux: number | null;
  demiJournees: number;
  absences: number;
  justifiees: number;
  presences: number;
  retards: number;
  nonJustifiees: number;
  heuresPrevues: number;
  heuresSuivies: number;
  auDelaDuSeuil: boolean;
}

export interface LigneAbsenteisme {
  cle: string;
  libelle: string;
  apprenantId: string | null;
  nom: string | null;
  prenom: string | null;
  sites: string[];
  formation: { code: string; titre: string } | null;
  promotion: { cle: string; libelle: string } | null;
  bloc: { id: string; libelle: string } | null;
  apprenants: number;
  mesure: MesureAbsenteisme;
}

export interface ReponseAbsenteisme {
  calculeLe: string;
  axe: AxeAbsenteisme;
  periode: { choix: ChoixPeriodeAbsenteisme; du: string | null; au: string | null; libelle: string };
  seuil: number;
  ensemble: { apprenants: number; auDelaDuSeuil: number; mesure: MesureAbsenteisme };
  lignes: LigneAbsenteisme[];
  feuillesOuvertes: number;
  options: {
    formations: { code: string; titre: string }[];
    promotions: { cle: string; libelle: string; siteCode: string; formation: string | null }[];
    axes: { axe: AxeAbsenteisme; libelle: string }[];
    periodes: { choix: ChoixPeriodeAbsenteisme; libelle: string }[];
  };
}

export interface FiltresAbsenteisme {
  site: string;
  axe: AxeAbsenteisme;
  periode: ChoixPeriodeAbsenteisme;
  du: string;
  au: string;
  formation: string;
  promotion: string;
  recherche: string;
  tri: TriAbsenteisme;
}

/**
 * Qui lit le taux d'absentéisme (TF-461) : le pôle pédagogique, assistant(e)
 * de site compris(e), l'administratif et la direction. La garde qui compte est
 * celle de l'API ; celle-ci évite un lien vers un écran refusé.
 */
export function peutLireAbsenteisme(profil: Profil | undefined): boolean {
  return profil === 'PEDAGOGIQUE' || profil === 'ADMINISTRATIF' || profil === 'DIRECTION';
}

export const CHEMIN_ABSENTEISME = '/ped-absences/absenteisme';

/** Une période libre incomplète ou à l'envers n'est pas demandée. */
export function periodeIncomplete(filtres: Pick<FiltresAbsenteisme, 'periode' | 'du' | 'au'>): boolean {
  return filtres.periode === 'LIBRE' && (!filtres.du || !filtres.au || filtres.du > filtres.au);
}

function chaine(filtres: FiltresAbsenteisme): string {
  const libre = filtres.periode === 'LIBRE';
  return parametresVueGlobale({ ...filtres, du: libre ? filtres.du : '', au: libre ? filtres.au : '' });
}

export function useAbsenteisme(filtres: FiltresAbsenteisme) {
  const parametres = chaine(filtres);
  return useQuery({
    queryKey: ['absenteisme', parametres],
    queryFn: () => appelApi<ReponseAbsenteisme>(`/absenteisme?${parametres}`),
    placeholderData: keepPreviousData,
    enabled: !periodeIncomplete(filtres),
  });
}

/**
 * Exporte en Excel (les quatre découpages) ou en PDF (le découpage affiché),
 * aux filtres de l'écran. Passe par `fetch` : un refus de l'API s'affiche à
 * l'écran au lieu de s'enregistrer comme un faux fichier.
 */
export async function exporterAbsenteisme(
  format: 'xlsx' | 'pdf',
  filtres: FiltresAbsenteisme,
): Promise<{ nomFichier: string }> {
  const reponse = await fetch(urlApi(`/absenteisme/export?${chaine(filtres)}`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ format }),
  });
  if (!reponse.ok) {
    const erreur = (await reponse.json().catch(() => null)) as
      | { erreur?: { code?: string; message?: string; details?: unknown } }
      | null;
    if (reponse.status === 401) window.dispatchEvent(new Event(EVENEMENT_SESSION_EXPIREE));
    throw new ErreurApi(
      reponse.status,
      erreur?.erreur?.code ?? 'ERREUR',
      erreur?.erreur?.message ?? 'L’export n’a pas pu être produit.',
      erreur?.erreur?.details,
    );
  }
  const nomFichier =
    /filename="([^"]+)"/.exec(reponse.headers.get('content-disposition') ?? '')?.[1] ?? `absenteisme.${format}`;
  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
  return { nomFichier };
}
